<template>
  <div class="referral-page__container">
    <referral-header>
      <section class="referral-header-cards">
        <referral-status-view-box 
          v-for="data in headerData"
          :key="data.code"
          :box-title="data.title"
          :box-sub-title="data.subtitle"
          :box-cnt="data.cnt"
          :box-cnt-format="data.cntFormat"
          :box-popover-message="data.popoverMessage"
          :box-event="data.event"
        />
      </section>
    </referral-header>

    <referral-body>
      <section class="referral-body-position__wrapper">
        <div class="referral-body-position__first-child">
          <referral-guide-box />
        </div>
        <div class="referral-body-position__second-child">
          <referral-action-with-input-box 
            box-title="초대 링크 공유"
            box-description="초대 링크를 복사해서 지인에게 공유해 주세요."
            box-input-button-label="링크 복사"
            :box-default-value="referralAddress"
            :box-is-input-disabled="true"
            :box-input-button-action="copyToClipBoard"
            :box-input-value-copy-event="detectDragCopy"
          />
          <referral-action-with-input-box 
            box-title="초대 이메일 전송"
            box-description="입력하신 이메일로 피플박스 초대 이메일을 보내드립니다."
            box-input-button-label="이메일 전송"
            box-input-placeholder="지인의 이메일을 하나씩 입력해 주세요"
            box-validate-error-message="정확한 이메일을 입력해주세요."
            :box-validate-pattern="/([\w-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/"
            :box-input-button-action="sendEmail"
            :box-input-multiple-value-action="blockMultipleEmail"
          />
        </div>
      </section>
    </referral-body>

    <referral-footer />

    <booking-exist-warning-dialog 
      title="가입 완료된 지인/파트너사 수"
      :message="'지금까지 귀하의 초대 링크를 통해 가입한 모든 지인 / 파트너사의 리스트입니다. 각 파트너사 별 피플박스 이용 현황은 아래와 같습니다.'"
      :dialog-visible="referralDialogVisible"
      confirm-button-text="확인"
      :cancel-button-text="null"
      :has-slot="true"
      :show-notice="false"
      @confirm="closeDialog()"
    >
      <ul>
        <li 
          class="referral-center-list" 
          v-for="center in referralCenterList"
          :key="center.id"
        >
          <span class="referral-center-list__name">{{ center.name }}</span>
          <span
            v-if="center.status === 'EXPIRED' || center.status === 'BLOCKED'"
            class="referral-center-list__status"
          >
            미이용
          </span>
          <span
            v-else-if="center.status === 'FREE'"
            class="referral-center-list__status"
          >
            무료 플랜 이용 중
          </span>
          <span
            v-else
            class="referral-center-list__status"
          >
            유료 플랜 전환 완료
          </span>
        </li>
      </ul>
    </booking-exist-warning-dialog>
  </div>
</template>

<script>
import ReferralBody from './ReferralBody';
import ReferralHeader from './ReferralHeader';
import ReferralFooter from './ReferralFooter';
import ReferralGuideBox from './ReferralGuideBox';
import ReferralStatusViewBox from './ReferralStatusViewBox';
import ReferralActionWithInputBox from './ReferralActionWithInputBox';
import BookingExistWarningDialog from '@/components/BookingExistWarningDialog';

import { mapGetters } from 'vuex';
import { fiflboxApi } from '@/utils/api';

const ReferralCode = {
  MY : {
    OVERALL: 1,
    RECEIVED: 2
  },
  TOTAL : {
    AVG : 3,
    MAX : 4
  }
}

export default {
  name: 'ReferralOverviewPage',
  components: { ReferralHeader,
    ReferralBody,
    ReferralFooter,
    ReferralStatusViewBox,
    ReferralGuideBox,
    BookingExistWarningDialog,
    ReferralActionWithInputBox },
  data(){
    return {
      referralCenterList: [],
      referralDialogVisible: false,
      referralData : {
        myOverall: {
          title : {
            emphasis:'가입 완료',
            normal:'지인 수'
          },
          subtitle: undefined,
          cnt: 0,
          cntFormat: '명',
          popoverMessage : '귀하의 초대 링크를 통해 가입을 완료된 모든 지인/<br/>파트너사 수입니다.',
          event: () => { this.referralDialogVisible = true; },
          code: ReferralCode.MY.OVERALL,
        },
        myReceived: {
          title : {
            emphasis:'누적 혜택',
            normal:'기간'
          },
          subtitle: undefined,
          cnt: 0,
          cntFormat: '개월',
          popoverMessage : '귀하가 본 초대 프로그램을 통해 제공받은 총 누적<br/>혜택 기간입니다.',
          event: () => {this.$router.push('/payment')},
          code: ReferralCode.MY.RECEIVED,
        },
        totalAvarage: {
          title : {
            emphasis:'참여 파트너사',
            normal: undefined,
          },
          subtitle: '평균 누적 혜택 기간',
          cnt: 3,
          cntFormat: '개월',
          popoverMessage : '참여 파트너사가 본 초대 프로그램을 통해 제공받은<br/>평균 누적 혜택 기간입니다.',
          event: undefined,
          code: ReferralCode.TOTAL.AVG,
        },
        totalMaximum: {
          title : {
            emphasis: '참여 파트너사',
            normal: undefined,
          },
          subtitle: '최대 누적 혜택 기간',
          cnt: 9,
          cntFormat: '개월',
          popoverMessage : '참여 파트너사 중 본 초대 프로그램을 통해 가장<br/>많이 제공받은 혜택 기간입니다.',
          event: undefined,
          code: ReferralCode.TOTAL.MAX,
        }
      }
    }
  },
  computed: {
    headerData(){
      return Object.values(this.referralData);
    },
    ...mapGetters({
      centerAccount: 'getCenterAccount',
    }),
    referralAddress(){
      return this.centerAccount.code ? `${process.env.VUE_APP_ROOT_URL}/#/register?referral_code=${this.centerAccount.code}` : undefined;
    },
  },
  async mounted(){
    const response = await fiflboxApi.v1.getReferralDashboardInfos();
    const { data } = response;
    const { received_month__max, received_month__avg, received_month, received_center } = data;
    this.referralData.myOverall.cnt = received_center.length;
    this.referralData.myReceived.cnt = received_month;
    this.referralData.totalAvarage.cnt = received_month__avg ? Math.round(received_month__avg) : 0;
    this.referralData.totalMaximum.cnt = received_month__max ? received_month__max : 0;
    this.referralCenterList = received_center;
  },
  methods:{
    closeDialog(){
      this.referralDialogVisible = false;
    },
    copyToClipBoard(){
      const tempTextArea = document.createElement('textarea');
      tempTextArea.value = this.referralAddress;
      document.body.appendChild(tempTextArea);
      
      tempTextArea.select();
      tempTextArea.setSelectionRange(0, 9999); 

      document.execCommand('copy');
      document.body.removeChild(tempTextArea);

      this.$message.warning('초대 링크를 복사했습니다.');
    },
    async sendEmail(target){
      const receiverMail = target.value;
      const payload = {
        referral_code : this.centerAccount.code,
        receiver_mail : [receiverMail],
      }
      try {
        this.$message.warning('초대 이메일을 전송합니다.');
        setTimeout(()=>{
          target.value = '';
        },100);
        await fiflboxApi.v1.postReferralInviteMail(payload);
      } catch (error) {
        const title = '전송 실패';
        const message = '이메일 전송에 실패했습니다. 다시 시도해 주세요.';
        this.$alert(message, title);
        throw Error('Mail Send Error : ', error);
      }
    },
    detectDragCopy(event){
      const selection = document.getSelection();
      const copyText = selection.toString();
      if(copyText === this.referralAddress){
        event.clipboardData.setData('text/plain', copyText);
        this.$message.warning('초대 링크를 복사했습니다.');
      }
      event.preventDefault();
    },
    blockMultipleEmail(target){
      if(target.value){
        let emails = null;
        const getEmailList = (value, filter) => {return value.split(filter).map(val=> val.replace(/\s/g, '')).filter(val=>val);};
        // detect1 : ,
        if(target.value.indexOf(',') > -1){
          emails = getEmailList(target.value, ',');
        }
        // detect2 : blank
        else{
          emails = getEmailList(target.value, ' ');
        }

        if(emails.length > 1){
          this.$alert('이메일 전송에 실패했습니다. 이메일을 하나씩 입력해 주세요.','전송 실패');
          target.value = '';
          return false;
        }
      }
      return true;
    },
  },
};

</script>
<style lang="scss">
.referral-page__container{
  .referral-header-cards{
    display: flex !important;
    @media screen and (max-width: 768px) {
      width: 100%;
      flex-wrap: wrap;
    }

    article{
      &:not(:last-child){
        margin-right: 6px;
      }
      @media screen and (max-width: 768px) {
        &:not(:last-child){
          margin-right: 0;
        }

        &:nth-child(odd){
          margin-right: 3px;
        }
        &:nth-child(even){
          margin-left: 3px;
        }
        &:nth-child(-n + 2){
          margin-bottom: 3px;
        }
        &:nth-child(n + 3){
          margin-top: 3px;
        }
      }
    }
  }

  .referral-body-position__wrapper{
    display: flex;
    flex-wrap: wrap;
    .referral-body-position__first-child{
      width: 406px;
      margin-right: 20px;
      @media screen and (max-width:768px) {
        width: 100%;
        margin-right: 0;
        margin-bottom: 13px;
      }
    }
    .referral-body-position__second-child{
      width: calc(100% - 426px);
      div:first-child{
        margin-bottom: 13px;
      }
      @media screen and (max-width:768px) {
        width: 100%;
      }
    }
  }

}

.booking-list-container{
  .referral-center-list{
    display: flex;
    span:first-child{
      width: calc(100% - 214px - 7px);
      margin-right: 7px;
      text-overflow: ellipsis;
      overflow: hidden;
      @media screen and (max-width: 768px) {
        width: calc(50% - 3.5px)
      }
    }
    span:last-child{
      width: 214px;
      @media screen and (max-width: 768px) {
        width: calc(50% - 3.5px)
      }
    }
  }
}
</style>